// SPDX-License-Identifier: LicenseRef-TRUMPF
// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//

import React from 'react';
import styled from 'styled-components';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import { Tag } from 'components/projectSelection/common/ProjectSettingTypes';
import { Button, Combobox, Divider, Tag as TreactTag } from '@tls/treact-ui';
import { IconPlus } from '@tls/treact-ui/icons';

export type TagInputProps = {
  allTagsAsObject: Tag[];
  selectedTag: Tag;
  setSelectedTag: (tag: Tag) => void;
  addSelectedTag: () => void;
  removeTag: (tag: string) => void;
  tags: string[];
  containerRef: React.RefObject<Element | null>;
  t: (key: string) => string;
  useCase?: UseCase;
};

export const TagInput = ({
  allTagsAsObject,
  selectedTag,
  setSelectedTag,
  addSelectedTag,
  removeTag,
  tags,
  containerRef,
  t,
  useCase,
}: Readonly<TagInputProps>) => (
  <>
    <Divider className='left-divider push-up' headline={t('project.tag.title')} label='' />

    <TwoColumns $useCase={useCase}>
      <Combobox
        options={allTagsAsObject}
        selectedOption={selectedTag}
        keySelector={tag => tag.label}
        labelSelector={tag => tag.label}
        noResultText=''
        onChange={setSelectedTag}
        containerRef={containerRef as React.RefObject<Element>}
        allowCustomValue
        className='no-shadow push-right'
        label={t('project.settings.tag.name')}
        data-testid='tag-input-combobox'
        dataTestIdSelector={tag => `tag-input-option-${allTagsAsObject.indexOf(tag) + 1 || 'unknown'}`}
      />
      <CenterButton>
        <Button
          variant='secondary'
          icon={<IconPlus />}
          label={t('project.settings.tag.add')}
          onClick={addSelectedTag}
          data-testid='tag-input-add-button'
        />
      </CenterButton>
    </TwoColumns>
    <TagContainer>
      {tags.map(t => (
        <TreactTag
          key={t}
          label={t}
          onClose={() => removeTag(t)}
          className='push-right'
          data-testid={`tag-input-tag-${t}-remove-button`}
        />
      ))}
    </TagContainer>
  </>
);

const TwoColumns = styled.div<{ $useCase?: UseCase }>`
  display: grid;
  grid-template-columns: ${({ $useCase }) =>
    $useCase === UseCase.QUALITY_CHECK ? 'repeat(3, 1fr) 38px' : 'repeat(2, 1fr)'};
  gap: 1rem;
`;

const CenterButton = styled.div`
  margin: auto 0;
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: -1rem;
`;
